/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 *
 * Provides access to the e-mail sender configuration resource of the
 * user-management service (bring-your-own sender per organisation).
 */

import {inject, Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {map, tap} from 'rxjs/operators';

import {environment} from '../environments/environment';
import {
  EmailSenderConfig,
  EmailSenderConfigRequest,
} from '../models/EmailSenderConfig';
import {ApiClient} from './api-client.service';
import {RequestCacheService} from './request-cache.service';

interface EmailSenderConfigResponse {
  readonly data: EmailSenderConfig | null;
}

const emailConfigUrl = (orgId: string) =>
  `${environment.backend.user_host}/organizations/${orgId}/email-config`;

@Injectable({
  providedIn: 'root',
})
export class EmailSenderConfigService {
  private readonly apiClient = inject(ApiClient);
  private readonly cache = inject(RequestCacheService);

  /** Returns the current configuration or `null` when the organisation uses the platform default. */
  load$(organizationId: string): Observable<EmailSenderConfig | null> {
    return this.apiClient
      .getShared<EmailSenderConfigResponse>(emailConfigUrl(organizationId))
      .pipe(map((response) => response.data));
  }

  /** Creates or replaces the configuration and invalidates the cache. */
  save$(
    organizationId: string,
    config: EmailSenderConfigRequest,
  ): Observable<EmailSenderConfig> {
    return this.apiClient
      .put<{data: EmailSenderConfig}>(emailConfigUrl(organizationId), config)
      .pipe(
        map((response) => response.data),
        tap(() => this.invalidateCache(organizationId)),
      );
  }

  /** Removes the configuration (falls back to the platform default) and invalidates the cache. */
  delete$(organizationId: string): Observable<void> {
    return this.apiClient
      .delete<void>(emailConfigUrl(organizationId))
      .pipe(tap(() => this.invalidateCache(organizationId)));
  }

  /** Sends a test e-mail with the given (unsaved) configuration to `recipient`. */
  sendTest$(
    organizationId: string,
    config: EmailSenderConfigRequest,
    recipient: string,
  ): Observable<void> {
    return this.apiClient
      .post<void>(`${emailConfigUrl(organizationId)}/test`, {
        config,
        recipient,
      })
      .pipe(tap(() => this.invalidateCache(organizationId)));
  }

  /** Re-checks the verification state (SES identity) and returns the refreshed config. */
  verify$(organizationId: string): Observable<EmailSenderConfig> {
    return this.apiClient
      .post<{
        data: EmailSenderConfig;
      }>(`${emailConfigUrl(organizationId)}/verify`, {})
      .pipe(
        map((response) => response.data),
        tap(() => this.invalidateCache(organizationId)),
      );
  }

  invalidateCache(organizationId: string): void {
    this.cache.invalidateByPrefix(emailConfigUrl(organizationId));
  }
}
